import { useEffect, useState } from "react";
import { ShieldCheck, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PLAN_LABELS = { demo: "Demo", basic: "Basic", pro: "Pro" };

/**
 * Sticky amber banner shown to the Super Admin while they manage another
 * business via the switcher. Click "Avslutt admin-modus" to return to the
 * ClientFlow HQ context. Renders nothing when:
 *   - the user is not a platform admin, or
 *   - the admin is currently managing their own business (no impersonation).
 */
export default function AdminImpersonationBanner() {
  const { isPlatformAdmin, adminBusinessId, setAdminBusinessId } = useAuth();
  const [bizName, setBizName] = useState("");
  const [plan, setPlan] = useState("");

  // Pull the human-readable name + plan of whichever business we're scoped
  // to right now. Reuses `/api/business`, which already honours the
  // impersonation header.
  useEffect(() => {
    if (!isPlatformAdmin || !adminBusinessId) {
      setBizName("");
      setPlan("");
      return;
    }
    let mounted = true;
    api.get("/business")
      .then((r) => {
        if (!mounted) return;
        setBizName(r.data?.name || "");
        setPlan(r.data?.plan || "");
      })
      .catch(() => { if (mounted) { setBizName(""); setPlan(""); } });
    return () => { mounted = false; };
  }, [isPlatformAdmin, adminBusinessId]);

  if (!isPlatformAdmin || !adminBusinessId) return null;

  const exit = () => {
    setAdminBusinessId(null);
    window.location.reload();
  };

  return (
    <div
      data-testid="admin-impersonation-banner"
      className="sticky top-0 z-40 border-b border-amber-300/60 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-800/60"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-2 flex items-center gap-3 text-xs sm:text-sm">
        <ShieldCheck className="w-4 h-4 text-amber-700 dark:text-amber-300 shrink-0" strokeWidth={1.75} />
        <div className="flex-1 min-w-0 text-amber-900 dark:text-amber-100">
          <span className="font-semibold uppercase tracking-wider mr-2 text-[10px] sm:text-xs">
            ClientFlow admin-modus
          </span>
          <span className="hidden sm:inline">Du administrerer: </span>
          <span className="font-semibold" data-testid="admin-impersonation-banner-business">
            {bizName || "kunde-konto"}
          </span>
          {plan && (
            <>
              <span className="mx-1.5 text-amber-700/60 dark:text-amber-300/60">·</span>
              <span
                className="font-medium"
                data-testid="admin-impersonation-banner-plan"
              >
                Plan: {PLAN_LABELS[plan] || plan}
              </span>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={exit}
          data-testid="admin-impersonation-banner-exit"
          className="inline-flex items-center gap-1 rounded-lg border border-amber-400/70 bg-white/70 dark:bg-amber-900/30 px-2.5 py-1 text-xs font-medium text-amber-900 dark:text-amber-100 hover:bg-white dark:hover:bg-amber-900/60 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Avslutt admin-modus</span>
          <span className="sm:hidden">Avslutt</span>
        </button>
      </div>
    </div>
  );
}
